import type { ReactNode } from "react";
import { FileText, Key, Layers, Send, Settings2, Zap } from "lucide-react";
import { StepInfoRow } from "../StepInfoRow";
import { getString, getNumber, getBool, isSensitiveHeader, maskHeaderValue } from "./helpers";
import type { SummaryTranslator } from "./types";

export function getApiLoadSummary(config: Record<string, unknown>, t: SummaryTranslator): ReactNode[] {
  const items: ReactNode[] = [];

  const url = getString(config.url);
  const domain = getString(config.domain);
  const protocol = getString(config.protocol) || "https";
  const method = getString(config.method) || "POST";
  const authResourceId = getString(config.authResourceId);
  const authResourceName = getString(config.authResourceName);
  const headers = config.headers as Record<string, string> | undefined;
  const batchEnabled = getBool(config.batchEnabled);
  const batchSize = getNumber(config.batchSize);
  const fieldMappings = Array.isArray(config.fieldMappings) ? config.fieldMappings : [];
  const parallelEnabled = getBool(config.parallelEnabled);
  const parallelWorkers = getNumber(config.parallelWorkers);

  const displayUrl = url?.startsWith("http") ? url : domain ? `${protocol}://${domain}${url || ""}` : url;

  if (displayUrl) {
    items.push(
      <div key="endpoint" className="flex items-start gap-2 text-xs">
        <span className="mt-0.5 shrink-0 text-gray-400"><Send className="size-3.5" /></span>
        <span className="inline-flex min-w-0 items-baseline gap-0 overflow-hidden rounded border border-indigo-200 dark:border-indigo-800">
          <span className="shrink-0 bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
            {method}
          </span>
          <span className="break-all px-1.5 py-0.5 font-mono text-[11px] text-gray-700 dark:text-gray-300">
            {displayUrl}
          </span>
        </span>
      </div>
    );
  }

  if (authResourceId) {
    const authDisplay = authResourceName || t("summaries.api_load.auth_resource_linked");
    items.push(
      <StepInfoRow key="auth" icon={<Key className="size-3.5" />} label={t("summaries.api_load.label_auth")} value={authDisplay} />
    );
  }

  if (headers && typeof headers === "object") {
    const headerEntries = Object.entries(headers);
    if (headerEntries.length > 0) {
      items.push(
        <div key="headers" className="space-y-0.5">
          <div className="flex items-center gap-2 text-xs">
            <span className="shrink-0 text-gray-400"><Settings2 className="size-3.5" /></span>
            <span className="text-gray-500 dark:text-gray-400">{t("summaries.api_load.label_headers", { count: headerEntries.length })}</span>
          </div>
          <div className="ml-5.5 space-y-px">
            {headerEntries.map(([key, value]) => (
              <div key={key} className="flex items-center gap-1 font-mono text-[11px]">
                <span className="shrink-0 text-gray-600 dark:text-gray-400">{key}:</span>
                <span className={`truncate ${isSensitiveHeader(key) ? "text-amber-600 dark:text-amber-400" : "text-gray-500"}`}>
                  {isSensitiveHeader(key) ? maskHeaderValue(String(value)) : String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
  }

  if (batchEnabled && batchSize) {
    items.push(
      <StepInfoRow key="batch" icon={<Layers className="size-3.5" />} label={t("summaries.api_load.label_batch")} value={t("summaries.api_load.batch_records", { count: batchSize })} />
    );
  }

  if (fieldMappings.length > 0) {
    items.push(
      <StepInfoRow key="mappings" icon={<FileText className="size-3.5" />} label={t("summaries.api_load.label_mappings")} value={t("summaries.api_load.mappings_count", { count: fieldMappings.length })} />
    );
  }

  if (parallelEnabled && parallelWorkers) {
    items.push(
      <StepInfoRow key="parallel" icon={<Zap className="size-3.5" />} label={t("summaries.api_load.label_parallel")} value={t("summaries.api_load.parallel_workers", { count: parallelWorkers })} />
    );
  }

  return items;
}
